import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Box, Button, Chip, Divider, InputBase, Link, Skeleton, Stack, Typography } from '@mui/material';
import { livyJson } from 'src/lib/livyApi';
const SEVERITY_COLOR = {
  low: 'default',
  normal: 'default',
  high: 'warning',
  urgent: 'error'
};
const STATUS_COLOR = {
  raised: 'info',
  filing: 'info',
  filed: 'success',
  // Confirmed, and Jira could not take it. Terminal, and worth looking at.
  held: 'warning',
  dismissed: 'default'
};
const FILTERS = ['all', 'raised', 'filing', 'filed', 'held', 'dismissed'];
const SECTION_LABEL = {
  fontSize: 10.5,
  letterSpacing: '.08em',
  textTransform: 'uppercase',
  color: 'text.secondary',
  mb: '3px'
};

/**
 * The log of everything asked of engineering through Livy.
 *
 * A list, not a queue. The decision moved to the confirmation card in the chat, next to the reply
 * that raised the request, so nothing here waits on an admin to press a second button.
 *
 * A `raised` row still offers one, and deliberately: the card lives in one conversation, and a
 * request whose chat has been closed - or one raised before the card existed, whose id predates
 * the current format - would otherwise have nowhere left to be confirmed. It is a way back to a
 * stranded request, not the path anyone is meant to take.
 */
export function RequestsTab() {
  const _useState = useState(null),
    _useState2 = _slicedToArray(_useState, 2),
    requests = _useState2[0],
    setRequests = _useState2[1];
  const _useState3 = useState(null),
    _useState4 = _slicedToArray(_useState3, 2),
    error = _useState4[0],
    setError = _useState4[1];
  const _useState5 = useState('all'),
    _useState6 = _slicedToArray(_useState5, 2),
    filter = _useState6[0],
    setFilter = _useState6[1];
  const _useState7 = useState(''),
    _useState8 = _slicedToArray(_useState7, 2),
    term = _useState8[0],
    setTerm = _useState8[1];
  const _useState9 = useState(null),
    _useState0 = _slicedToArray(_useState9, 2),
    busy = _useState0[0],
    setBusy = _useState0[1];
  const load = useCallback(() => {
    livyJson('/requests').then(d => setRequests(d.requests)).catch(e => setError(e.message));
  }, []);
  useEffect(load, [load]);
  const confirm = async row => {
    const sk = row.sk;
    setBusy(sk);
    setError(null);
    try {
      const _await$livyJson = await livyJson('/requests', {
          method: 'POST',
          // Same guard the chat card uses: the write refuses a revision that has moved on, so a row
          // this list read before an amend cannot file what it is still showing.
          body: {
            sk,
            decision: 'confirmed',
            expectedRevision: row.revision
          }
        }),
        request = _await$livyJson.request,
        jiraError = _await$livyJson.jiraError,
        stale = _await$livyJson.stale;
      if (stale) setError('That request changed since this list loaded. Refresh and read it again.');else if (jiraError) setError(jiraError);
      setRequests(list => (list !== null && list !== void 0 ? list : []).map(r => r.sk === sk ? _objectSpread(_objectSpread({}, r), request) : r));
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(null);
    }
  };
  const counts = useMemo(() => {
    var _requests$length;
    const tally = {
      all: (_requests$length = requests === null || requests === void 0 ? void 0 : requests.length) !== null && _requests$length !== void 0 ? _requests$length : 0
    };
    for (const r of requests !== null && requests !== void 0 ? requests : []) {
      var _r$status, _tally$status;
      const status = (_r$status = r.status) !== null && _r$status !== void 0 ? _r$status : 'raised';
      tally[status] = ((_tally$status = tally[status]) !== null && _tally$status !== void 0 ? _tally$status : 0) + 1;
    }
    return tally;
  }, [requests]);
  const shown = useMemo(() => {
    const needle = term.trim().toLowerCase();
    return (requests !== null && requests !== void 0 ? requests : []).filter(r => {
      var _r$status2, _r$problem;
      if (filter !== 'all' && ((_r$status2 = r.status) !== null && _r$status2 !== void 0 ? _r$status2 : 'raised') !== filter) return false;
      if (!needle) return true;
      return [r.title, (_r$problem = r.problem) !== null && _r$problem !== void 0 ? _r$problem : r.description, r.raisedBy, r.jiraKey, r.type].some(field => field === null || field === void 0 ? void 0 : field.toLowerCase().includes(needle));
    });
  }, [requests, filter, term]);
  if (error && !requests) return /*#__PURE__*/React.createElement(Alert, {
    severity: "error",
    sx: {
      mt: '16px'
    }
  }, error);
  if (!requests) return /*#__PURE__*/React.createElement(Stack, {
    spacing: "8px",
    sx: {
      mt: '16px'
    }
  }, [0, 1, 2].map(i => /*#__PURE__*/React.createElement(Skeleton, {
    key: i,
    height: 64
  })));
  return /*#__PURE__*/React.createElement(Box, {
    sx: {
      pt: '16px',
      maxWidth: 860
    }
  }, /*#__PURE__*/React.createElement(Typography, {
    variant: "body2",
    color: "text.secondary",
    sx: {
      mb: '14px'
    }
  }, "Everything asked of engineering through Livy, in this console or in support. The person who raised one confirms it on the card in their own chat; confirming files it into Jira and fills in the implicated services from the knowledge graph."), /*#__PURE__*/React.createElement(Stack, {
    direction: "row",
    spacing: "8px",
    sx: {
      mb: '14px',
      flexWrap: 'wrap',
      rowGap: '8px',
      alignItems: 'center'
    }
  }, FILTERS.map(f => {
    var _counts$f;
    return /*#__PURE__*/React.createElement(Chip, {
      key: f,
      size: "small",
      label: "".concat(f, " ").concat((_counts$f = counts[f]) !== null && _counts$f !== void 0 ? _counts$f : 0),
      onClick: () => setFilter(f),
      variant: filter === f ? 'filled' : 'outlined',
      color: filter === f ? 'primary' : 'default',
      sx: {
        textTransform: 'capitalize'
      }
    });
  }), /*#__PURE__*/React.createElement(InputBase, {
    value: term,
    onChange: e => setTerm(e.target.value),
    placeholder: "Filter",
    inputProps: {
      'aria-label': 'Filter requests'
    },
    sx: {
      border: 1,
      borderColor: 'divider',
      borderRadius: '8px',
      px: '10px',
      py: '2px',
      fontSize: 13,
      ml: 'auto'
    }
  })), error && /*#__PURE__*/React.createElement(Alert, {
    severity: "error",
    sx: {
      mb: '12px'
    },
    onClose: () => setError(null)
  }, error), shown.length === 0 ? /*#__PURE__*/React.createElement(Typography, {
    variant: "body2",
    color: "text.secondary"
  }, requests.length === 0 ? 'Nothing raised yet. Ask Livy to file a bug and it will write one up for you.' : 'Nothing matches that filter.') : /*#__PURE__*/React.createElement(Stack, {
    spacing: "10px"
  }, shown.map(r => {
    var _r$sk, _SEVERITY_COLOR$r$sev, _STATUS_COLOR, _r$status3, _r$status4, _r$raisedBy, _r$problem2, _r$status5, _r$status6;
    return /*#__PURE__*/React.createElement(Box, {
      key: (_r$sk = r.sk) !== null && _r$sk !== void 0 ? _r$sk : "".concat(r.at, "-").concat(r.title),
      sx: {
        border: 1,
        borderColor: 'divider',
        borderRadius: '10px',
        p: '14px'
      }
    }, /*#__PURE__*/React.createElement(Stack, {
      direction: "row",
      spacing: "8px",
      alignItems: "center",
      sx: {
        mb: '6px',
        flexWrap: 'wrap',
        rowGap: '6px'
      }
    }, /*#__PURE__*/React.createElement(Chip, {
      size: "small",
      variant: "outlined",
      label: r.type
    }), /*#__PURE__*/React.createElement(Chip, {
      size: "small",
      variant: "outlined",
      color: (_SEVERITY_COLOR$r$sev = SEVERITY_COLOR[r.severity]) !== null && _SEVERITY_COLOR$r$sev !== void 0 ? _SEVERITY_COLOR$r$sev : 'default',
      label: r.severity
    }), /*#__PURE__*/React.createElement(Chip, {
      size: "small",
      color: (_STATUS_COLOR = STATUS_COLOR[(_r$status3 = r.status) !== null && _r$status3 !== void 0 ? _r$status3 : 'raised']) !== null && _STATUS_COLOR !== void 0 ? _STATUS_COLOR : 'default',
      variant: r.status === 'filed' ? 'filled' : 'outlined',
      label: (_r$status4 = r.status) !== null && _r$status4 !== void 0 ? _r$status4 : 'raised'
    }), r.jiraKey && (r.jiraUrl ? /*#__PURE__*/React.createElement(Link, {
      href: r.jiraUrl,
      target: "_blank",
      rel: "noreferrer",
      underline: "hover",
      sx: {
        fontSize: 12.5
      }
    }, r.jiraKey) : /*#__PURE__*/React.createElement(Chip, {
      size: "small",
      color: "primary",
      label: r.jiraKey
    })), r.estimate && /*#__PURE__*/React.createElement(Chip, {
      size: "small",
      variant: "outlined",
      label: "~".concat(r.estimate, " with Claude Code")
    })), /*#__PURE__*/React.createElement(Typography, {
      sx: {
        fontSize: 14.5,
        fontWeight: 600,
        mb: '3px'
      }
    }, r.title), /*#__PURE__*/React.createElement(Typography, {
      sx: {
        fontSize: 11.5,
        color: 'text.secondary',
        mb: '10px'
      }
    }, (_r$raisedBy = r.raisedBy) !== null && _r$raisedBy !== void 0 ? _r$raisedBy : 'unknown', r.raisedByRole && r.raisedByRole !== 'unknown' ? " \xB7 ".concat(r.raisedByRole) : '', " \xB7", ' ', new Date(r.at).toLocaleString(), r.conversationId && /*#__PURE__*/React.createElement(React.Fragment, null, ' · ', /*#__PURE__*/React.createElement(Link, {
      href: "?tab=usage&conversation=".concat(encodeURIComponent(r.conversationId)),
      underline: "hover"
    }, "view chat")), r.decidedBy && " \xB7 ".concat(r.status, " by ").concat(r.decidedBy)), r.heldReason && /*#__PURE__*/React.createElement(Alert, {
      severity: "warning",
      variant: "outlined",
      sx: {
        mb: '10px',
        fontSize: 12.5
      }
    }, r.heldReason), /*#__PURE__*/React.createElement(Stack, {
      spacing: "10px"
    }, [['Problem', (_r$problem2 = r.problem) !== null && _r$problem2 !== void 0 ? _r$problem2 : r.description], ['Plan', r.plan], ['Success criteria', r.successCriteria]].map(_ref => {
      let _ref2 = _slicedToArray(_ref, 2),
        label = _ref2[0],
        value = _ref2[1];
      return value ? /*#__PURE__*/React.createElement(Box, {
        key: label
      }, /*#__PURE__*/React.createElement(Typography, {
        sx: SECTION_LABEL
      }, label), /*#__PURE__*/React.createElement(Typography, {
        variant: "body2",
        sx: {
          whiteSpace: 'pre-wrap'
        }
      }, value)) : null;
    })), ['raised', 'filing', 'held'].includes((_r$status5 = r.status) !== null && _r$status5 !== void 0 ? _r$status5 : 'raised') && /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Divider, {
      sx: {
        my: '12px'
      }
    }), /*#__PURE__*/React.createElement(Stack, {
      direction: "row",
      spacing: "10px",
      alignItems: "center",
      sx: {
        flexWrap: 'wrap',
        rowGap: '6px'
      }
    }, /*#__PURE__*/React.createElement(Button, {
      size: "small",
      variant: "outlined",
      disabled: busy === r.sk,
      onClick: () => void confirm(r),
      sx: {
        textTransform: 'none'
      }
    }, ((_r$status6 = r.status) !== null && _r$status6 !== void 0 ? _r$status6 : 'raised') === 'raised' ? 'Confirm and file' : 'Try filing again'), /*#__PURE__*/React.createElement(Typography, {
      variant: "caption",
      color: "text.secondary"
    }, r.status === 'filing' ? 'Filing did not finish. A retry is accepted once the previous attempt has timed out, about two minutes.' : r.status === 'held' ? 'Jira could not take this one. Retry once the reason above is fixed.' : 'Normally confirmed on the card in the chat that raised it.'))), r.repos && r.repos.length > 0 && /*#__PURE__*/React.createElement(Stack, {
      direction: "row",
      spacing: "6px",
      sx: {
        flexWrap: 'wrap',
        rowGap: '6px',
        mt: '10px'
      }
    }, r.repos.map(repo => /*#__PURE__*/React.createElement(Chip, {
      key: repo,
      size: "small",
      variant: "outlined",
      label: repo,
      sx: {
        height: 20
      }
    }))));
  })));
}